import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard, { formatViews } from "@/components/feature/ComicCard";
import SectionTitle from "@/components/feature/SectionTitle";
import Hero from "./components/Hero";
import { comics, categories, hotComics, newComics } from "@/mocks/comics";

const faqItems = [
  {
    q: "喵趣漫画需要付费吗？",
    a: "目前喵趣漫画的养猫日常、生活日常与日常趣事作品均可免费在线观看，轻松日常内容会持续更新，让每一位铲屎官都能随时随地方便地阅读。",
  },
  {
    q: "多久更新一次新作品？",
    a: "喵趣漫画的日常漫画作品基本保持每日或隔日更新，热门养猫日常作品更新更及时。你可以在首页「最新更新」区块第一时间看到新话内容。",
  },
  {
    q: "在手机和电脑上都能看吗？",
    a: "可以。喵趣漫画网采用响应式设计，无论你用手机、平板还是电脑，都能获得流畅舒适的阅读体验，随时随地在线看你的日常漫画。",
  },
  {
    q: "我可以投稿自己的养猫漫画吗？",
    a: "非常欢迎！如果你也喜欢用漫画记录养猫日常与生活趣事，可以联系我们投稿合作。我们会认真阅读每一份作品，让更多养猫人看到你的创作。",
  },
];

const steps = [
  { icon: "ri-eye-line", title: "每天一更", desc: "养猫日常与日常趣事持续上新" },
  { icon: "ri-heart-line", title: "温暖治愈", desc: "轻松日常内容解压又暖心" },
  { icon: "ri-smartphone-line", title: "随时可看", desc: "手机平板电脑多端流畅阅读" },
  { icon: "ri-group-line", title: "铲屎官同好", desc: "和千万养猫人一起追更交流" },
];

export default function Home() {
  const ranked = [...comics].sort((a, b) => b.views - a.views).slice(0, 6);

  return (
    <div className="bg-background-50 min-h-screen">
      <Navbar />
      <Hero />

      {/* 分类 */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
        <SectionTitle
          title="主题分类"
          subtitle="喵趣漫画养猫日常 · 生活日常 · 日常趣事"
          action={{ label: "查看全部", to: "/category" }}
        />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {categories.map((cat) => (
            <Link
              key={cat.key}
              to="/category"
              className="group rounded-2xl bg-background-100/70 border border-background-200 p-5 md:p-6 hover:border-primary-300 hover:bg-background-100 transition-colors"
            >
              <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500/10 text-primary-600 text-2xl mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                <i className={cat.icon}></i>
              </span>
              <h3 className="font-heading text-lg text-foreground-950">{cat.name}</h3>
              <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{cat.desc}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs text-primary-600">
                查看作品 <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* 热门推荐 */}
      <section className="bg-background-100/60 border-y border-background-200">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
          <SectionTitle
            title="热门推荐"
            subtitle="最受欢迎的喵趣漫画养猫日常作品"
            action={{ label: "全部热门", to: "/category" }}
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {hotComics.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </div>
      </section>

      {/* 人气榜单 */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
        <SectionTitle
          title="人气榜单"
          subtitle="本周最受铲屎官欢迎的日常漫画 Top 6"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {ranked.map((c, idx) => (
            <Link
              key={c.id}
              to={`/comic/${c.id}`}
              className="flex items-center gap-4 rounded-2xl bg-background-50 border border-background-200 p-3 hover:border-primary-300 transition-colors"
            >
              <span
                className={`w-10 h-10 flex items-center justify-center rounded-xl font-heading text-lg shrink-0 ${
                  idx === 0
                    ? "bg-accent-500 text-background-50"
                    : idx < 3
                      ? "bg-primary-500/15 text-primary-700"
                      : "bg-background-100 text-foreground-600"
                }`}
              >
                {idx + 1}
              </span>
              <img
                src={c.cover}
                alt={`${c.title} 榜单封面`}
                className="w-12 h-16 rounded-lg object-cover shrink-0"
              />
              <span className="min-w-0 flex-1">
                <span className="block font-heading text-base text-foreground-950 truncate">
                  {c.title}
                </span>
                <span className="block text-xs text-foreground-600 mt-0.5">
                  {c.category} · {formatViews(c.views)}人气
                </span>
              </span>
              <span className="text-foreground-400 shrink-0">
                <i className="ri-fire-line text-primary-500"></i>
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* 最新更新 */}
      <section className="bg-background-100/60 border-y border-background-200">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
          <SectionTitle
            title="最新更新"
            subtitle="喵趣漫画日常趣事 · 追更不停歇"
            action={{ label: "全部作品", to: "/category" }}
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {newComics.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </div>
      </section>

      {/* 品牌理念 */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
        <div className="rounded-3xl bg-gradient-to-br from-primary-500 to-primary-700 text-background-50 p-8 md:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl leading-tight">
                把养猫的快乐
                <span className="block text-accent-500">画给你看</span>
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-background-50/90 max-w-md">
                喵趣漫画相信，养猫日常里藏着最真实的温柔与幽默。我们用心挑选每一部喵趣漫画养猫日常、生活日常与日常趣事作品，让轻松快乐常伴你的阅读时光。
              </p>
              <button
                onClick={() => window.REACT_APP_NAVIGATE("/about")}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-background-50 text-primary-700 text-sm px-6 py-2.5 whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
              >
                了解更多 <i className="ri-arrow-right-line"></i>
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4 md:gap-5">
              {steps.map((s) => (
                <div
                  key={s.title}
                  className="rounded-2xl bg-background-50/10 backdrop-blur border border-background-50/20 p-5"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-50 text-primary-600 text-xl mb-3">
                    <i className={s.icon}></i>
                  </span>
                  <h3 className="font-heading text-lg">{s.title}</h3>
                  <p className="mt-1 text-xs text-background-50/80">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-4 md:px-6 pb-16 md:pb-24">
        <SectionTitle title="常见问题" subtitle="关于喵趣漫画网，你可能想知道的" />
        <div className="space-y-3">
          {faqItems.map((item) => (
            <details
              key={item.q}
              className="group rounded-2xl bg-background-100/70 border border-background-200 px-5 py-4 open:bg-background-100"
            >
              <summary className="flex items-center justify-between cursor-pointer list-none">
                <span className="font-medium text-foreground-950 text-sm md:text-base">
                  {item.q}
                </span>
                <i className="ri-add-line text-primary-600 group-open:rotate-45 transition-transform"></i>
              </summary>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{item.a}</p>
            </details>
          ))}
        </div>
        <div className="mt-6 text-center">
          <button
            onClick={() => window.REACT_APP_NAVIGATE("/faq")}
            className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer"
          >
            查看全部常见问题 <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}